/**
 * Authed app chrome for the wallet's management screens: a top bar with the SCP
 * Wallet lockup, primary nav, and sign-out, over a centered content column.
 * Pre-auth screens (login/register/not-supported) use the simpler AppShell.
 */
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWallet } from '~/wallet';
import { LogoLockup } from './Logo';
import { Button } from './primitives';

const NAV = [
  { to: '/account', label: 'Account' },
  { to: '/applications', label: 'Applications' },
  { to: '/delegations', label: 'Delegations' },
  { to: '/settings', label: 'Settings' },
];

export function AppPage({ title, actions, children }: { title?: string; actions?: ReactNode; children: ReactNode }) {
  const { identity } = useWallet();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  function signOut() {
    identity.logout();
    navigate('/login');
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-default)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-6)',
          padding: '0 var(--space-6)',
          height: 64,
          background: 'var(--surface-contrast)',
          borderBottom: '1px solid var(--stroke-on-low)',
        }}
      >
        <Link to="/account" style={{ textDecoration: 'none' }} aria-label="SCP Wallet — Account">
          <LogoLockup height={24} />
        </Link>
        <nav style={{ display: 'flex', gap: 'var(--space-1)', flex: 1 }}>
          {NAV.map((item) => {
            const active = pathname === item.to || pathname.startsWith(item.to + '/');
            return (
              <Link
                key={item.to}
                to={item.to}
                style={{
                  font: '500 14px/1 var(--font-sans)',
                  color: active ? 'var(--c-violet)' : 'var(--text-secondary)',
                  background: active ? 'var(--c-violet-light)' : 'transparent',
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <Button variant="tertiary" size="sm" fullWidth={false} onClick={signOut}>
          Sign out
        </Button>
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--space-8) var(--space-6)' }}>
        {(title || actions) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-6)',
            }}
          >
            {title && (
              <h1 style={{ margin: 0, font: '600 28px/1.2 var(--font-display)', letterSpacing: '-0.01em' }}>{title}</h1>
            )}
            {actions}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>{children}</div>
      </main>
    </div>
  );
}
